import { useEffect, useState } from 'react';
import { Toaster, toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import CrudModal from '../../components/admin/CrudModal';
import { adminDelete, adminGet, adminPost, adminPut } from '../../lib/adminApi';

const emptyIntervention = {
  reference: '',
  client: '',
  equipment: '',
  type: '',
  technician_id: '',
  technician_name: '',
  status: 'a_planifier',
  scheduled_date: '',
  due_date: '',
  amount: 0,
  notes: '',
};

const AdminInterventions = () => {
  const [items, setItems] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formState, setFormState] = useState(emptyIntervention);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      const [interventionsData, techniciansData] = await Promise.all([
        adminGet('/admin/interventions'),
        adminGet('/admin/technicians'),
      ]);
      setItems(interventionsData);
      setTechnicians(techniciansData);
    } catch (error) {
      console.error('Error fetching interventions:', error);
      toast.error('Erreur lors du chargement des interventions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreateModal = () => {
    setEditingItem(null);
    setFormState(emptyIntervention);
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormState({ ...emptyIntervention, ...item });
    setModalOpen(true);
  };

  const handleChange = (field, value) => {
    if (field === 'technician_id') {
      const selected = technicians.find((item) => item.id === value);
      setFormState((current) => ({
        ...current,
        technician_id: value,
        technician_name: selected ? selected.name : '',
      }));
      return;
    }
    setFormState((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...formState, amount: Number(formState.amount || 0) };
      if (editingItem) {
        await adminPut(`/admin/interventions/${editingItem.id}`, payload);
        toast.success('Intervention mise à jour');
      } else {
        await adminPost('/admin/interventions', payload);
        toast.success('Intervention créée');
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving intervention:', error);
      toast.error('Erreur lors de l’enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Supprimer cette intervention ?')) return;
    try {
      await adminDelete(`/admin/interventions/${itemId}`);
      toast.success('Intervention supprimée');
      fetchData();
    } catch (error) {
      console.error('Error deleting intervention:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="crm-loading">
          <div className="crm-spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout onAction={openCreateModal}>
      <Toaster position="top-right" richColors />
      <section className="crm-card">
        <div className="crm-section-head">
          <div>
            <h2 className="crm-section-title">Interventions</h2>
            <p className="crm-section-subtitle">
              Suivi opérationnel des dossiers techniques et affectations.
            </p>
          </div>
        </div>

        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead>
              <tr>
                <th>Référence</th>
                <th>Client</th>
                <th>Type</th>
                <th>Technicien</th>
                <th>Statut</th>
                <th>Échéance</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan="7"><div className="crm-empty">Aucune intervention enregistrée.</div></td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.reference}</td>
                    <td>{item.client}</td>
                    <td>{item.type}</td>
                    <td>{item.technician_name || 'Non affecté'}</td>
                    <td>{item.status}</td>
                    <td>{item.due_date}</td>
                    <td>
                      <div className="crm-table__actions">
                        <button type="button" className="crm-action-link" onClick={() => openEditModal(item)}>Modifier</button>
                        <button type="button" className="crm-action-danger" onClick={() => handleDelete(item.id)}>Supprimer</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {modalOpen ? (
        <CrudModal
          title={editingItem ? 'Modifier une intervention' : 'Créer une intervention'}
          onClose={() => setModalOpen(false)}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel={editingItem ? 'Mettre à jour' : 'Créer'}
        >
          <div className="crm-form__grid">
            <div className="crm-form__field">
              <label>Référence</label>
              <input value={formState.reference} onChange={(e) => handleChange('reference', e.target.value)} required />
            </div>
            <div className="crm-form__field">
              <label>Client</label>
              <input value={formState.client} onChange={(e) => handleChange('client', e.target.value)} required />
            </div>
            <div className="crm-form__field">
              <label>Engin / panne</label>
              <input value={formState.equipment} onChange={(e) => handleChange('equipment', e.target.value)} />
            </div>
            <div className="crm-form__field">
              <label>Type</label>
              <input value={formState.type} onChange={(e) => handleChange('type', e.target.value)} required />
            </div>
            <div className="crm-form__field">
              <label>Technicien</label>
              <select value={formState.technician_id} onChange={(e) => handleChange('technician_id', e.target.value)}>
                <option value="">Non affecté</option>
                {technicians.map((technician) => (
                  <option key={technician.id} value={technician.id}>{technician.name}</option>
                ))}
              </select>
            </div>
            <div className="crm-form__field">
              <label>Statut</label>
              <select value={formState.status} onChange={(e) => handleChange('status', e.target.value)}>
                <option value="a_planifier">À planifier</option>
                <option value="en_cours">En cours</option>
                <option value="atelier">Atelier</option>
                <option value="terminee">Terminée</option>
              </select>
            </div>
            <div className="crm-form__field">
              <label>Date planifiée</label>
              <input type="date" value={formState.scheduled_date || ''} onChange={(e) => handleChange('scheduled_date', e.target.value)} />
            </div>
            <div className="crm-form__field">
              <label>Échéance</label>
              <input type="date" value={formState.due_date} onChange={(e) => handleChange('due_date', e.target.value)} required />
            </div>
            <div className="crm-form__field">
              <label>Montant</label>
              <input type="number" min="0" value={formState.amount} onChange={(e) => handleChange('amount', e.target.value)} />
            </div>
            <div className="crm-form__field crm-form__field--full">
              <label>Notes</label>
              <textarea value={formState.notes} onChange={(e) => handleChange('notes', e.target.value)} />
            </div>
          </div>
        </CrudModal>
      ) : null}
    </AdminLayout>
  );
};

export default AdminInterventions;
